<!--
###########################################
 script setup
###########################################
-->
<script setup lang="ts">
import { ref } from "vue";
import "gitart-vue-dialog/dist/style.css";
import { GDialog } from "gitart-vue-dialog";
import DeliveryPlanEvent from "@/types/DeliveryPlanEvent";
import WorkloadType from "@/types/Workload"
import getWorkloadResponse from "@/types/responses/getWorkload"
import Api from "@/services/api/ApiServiceFabrick";
import { SELECT_WORK_EVENT_TYPES } from "@/services/consts";
import { useAuth } from "@/services/user/Auth0UserProvider"
import Workload from '@/services/models/Workload'
import ModalFlashMessage from "@/components/Common/ModalFlashMessage.vue";
import i18n from '@/lang/i18n';

// const
const MAX_TABLE_ROW:any = {"loading": 3, "unloading": 3, "refueling": 1}
const selectWorkEventTypes:any = SELECT_WORK_EVENT_TYPES

// reactive
const items = ref<WorkloadType[]>([]);
const selectableWorkloads = ref();
const selectableUnits = ref();

const errorMessages = ref()
const dialogState = ref(false);
const submitButtonState = ref(false);
const loadingState = ref(false);
const flashMessage = ref({
  "message": "",
  "show": false,
  "type": "info",
  "cancel": false
});

// variable
const { loginWithRedirect } = useAuth();
let workId = "";
let deleteItems:number[] = [];

const props = defineProps<{
  isWaitingForSave: boolean,
  workItem: DeliveryPlanEvent,
  operationOfficeId: number,
  updateAuthority: boolean
}>()
const emit = defineEmits<{
  (e: 'setWorkId', workId: string): void
  (e: 'setRefuelingAmount'): void
}>()

const initModal = () => {
  items.value = [];
  deleteItems = [];
  selectableWorkloads.value = [];
  selectableUnits.value = [];
  errorMessages.value = [];
  flashMessage.value["show"] = false;
  flashMessage.value["cancel"] = true;
};
const openModal = () => {
  dialogState.value = true;
  if(!props.isWaitingForSave){
    loadingState.value = false;
    initModal();
    if(!props.workItem.work_id){
      generateWorkId()
    }else{
      workId = props.workItem.work_id
      getWorkloads(workId);
    }
  }
};
const closeModal = () => {
  dialogState.value = false;
};
const submit = () => {
  submitButtonState.value = true;
  const isValidate = Workload.validate(props.workItem.work, items.value);
  if(Object.keys(isValidate).length > 0){
    errorMessages.value = isValidate
    submitButtonState.value = false;
  }else{
    errorMessages.value = []
    setWorkloads(workId)
  }
};
const addTableRow = () => {
  let init_unit = i18n.global.t('pages.dailyReports.workloadUpdateModal.pieces');
  if(props.workItem.work == "refueling"){
    init_unit = i18n.global.t('pages.dailyReports.workloadUpdateModal.liter')
  }
  const emptyItem: WorkloadType = {
    id: null,
    work_id: workId,
    delivery_plan_id: props.workItem.delivery_plan_id,
    operation_office_id: props.operationOfficeId,
    name: "",
    quantity: null,
    unit: init_unit,
    note: null,
    updated_by: null,
    created_by: null,
  }
  items.value.push(emptyItem)
};
const removeTableRow = (item: WorkloadType, index:number) => {
  items.value.splice(index, 1);
  if(item.id != null) deleteItems.push(item.id);
  removeErrorMessageRow(index)
};
const removeErrorMessageRow = (index:number) => {
  if(Object.keys(errorMessages.value).length > 0){
    for(let i=index; i < 3; i++){
      if(i < 2){
        errorMessages.value['workloads.' + i + '.name'] = errorMessages.value['workloads.' + (i+1) + '.name']
        errorMessages.value['workloads.' + i + '.quantity'] = errorMessages.value['workloads.' + (i+1) + '.quantity']
        errorMessages.value['workloads.' + i + '.unit'] = errorMessages.value['workloads.' + (i+1) + '.unit']
        errorMessages.value['workloads.' + i + '.note'] = errorMessages.value['workloads.' + (i+1) + '.note']
      }else{
        delete errorMessages.value['workloads.' + i + '.name']
        delete errorMessages.value['workloads.' + i + '.quantity']
        delete errorMessages.value['workloads.' + i + '.unit']
        delete errorMessages.value['workloads.' + i + '.note']
      }
    }
  }
};

const generateWorkId = () => {
  Workload.generateWorkId(
    props.workItem.works[0].delivery_plan_id,
    props.workItem.works[0].work,
    props.workItem.works[0].status,
    props.workItem.works[0].date_time
  ).then((digestHex) => {
    workId = digestHex
    getWorkloads(workId);
  });
}
const getWorkloads = (workId:string) => {
  Api().getWorkloads(props.operationOfficeId, workId).then((data:getWorkloadResponse) => {
    data.workloads.map((item)=>{
      item.quantity = Number(item.quantity)
    })
    loadingState.value = true;
    items.value = data.workloads;
    selectableWorkloads.value = data.selectable_workloads;
    selectableUnits.value = data.selectable_units;
    if(items.value.length == 0){
      addTableRow()
    }
  }).catch((error:any) => {
    const res = error.response
    switch (res.status) {
      case 401:
        loginWithRedirect({ appState: { targetUrl: location.pathname } })
        break
      case 403:
        location.reload()
        break
      case 500:
        flashMessage.value = {
          message: i18n.global.t('pages.dailyReports.errorMessage.getData'),
          show: true,
          type: "error",
          cancel: false
        }
        break
    }
  })
}
const setWorkloads = (workId:string) => {
  Api().upsertWorkloads(props.operationOfficeId, workId, props.workItem.works[0], items.value, deleteItems).then((data:getWorkloadResponse) => {
    data.workloads.map((item)=>{
      item.quantity = Number(item.quantity)
    })
    items.value = data.workloads;
    deleteItems = [];
    selectableWorkloads.value = data.selectable_workloads;
    selectableUnits.value = data.selectable_units;
    submitButtonState.value = false;
    emit('setWorkId', workId)
    flashMessage.value = {
      message: i18n.global.t('pages.dailyReports.messages.registered'),
      show: true,
      type: "info",
      cancel: false
    }
    setRefuelingAmount()
  }).catch((error:any) => {
    console.log(error)
    submitButtonState.value = false;
    const res = error.response
    switch (res.status) {
      case 401:
        loginWithRedirect({ appState: { targetUrl: location.pathname } })
        break
      case 403:
        location.reload()
        break
      case 422:
        errorMessages.value = res.data.errors
        break
      case 500:
        flashMessage.value = {
          message: i18n.global.t('pages.dailyReports.errorMessage.registered'),
          show: true,
          type: "error",
          cancel: false
        }
        break
    }
  })
}
const setRefuelingAmount = () => {
  if(props.workItem.works[0].work == "refueling"){
    emit('setRefuelingAmount')
  }
}
</script>

<!--
###########################################
 template
###########################################
-->
<template>
  <template v-if="!props.isWaitingForSave">
    <GDialog
      v-model="dialogState"
      max-width="90%"
      transition="custom-from-bottom-transition"
    >
      <div class="workload-update-modal">
        <!-- HEADER -->
        <div class="row mt-3 ml-6 mr-6">
          <h4>
            {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.loadingOrRefuelingRegistration') }}
          </h4>
        </div>

        <v-divider class="mt-2 mb-0"></v-divider>

        <ModalFlashMessage
          :message = "flashMessage.message"
          :show = "flashMessage.show"
          :type = "flashMessage.type"
          :cancel = "flashMessage.cancel"
          @setShow = "(show:boolean) => {flashMessage.show = show}"
        ></ModalFlashMessage>

        <!-- BODY -->
        <div class="row ml-6 mr-6">
          <div class="col-2">
            {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.work') }}
          </div>
          <div class="col-10">{{ selectWorkEventTypes[workItem.work] }}</div>
        </div>
        <div class="row ml-6 mr-6">
          <div class="col-2">
            {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.address') }}
          </div>
          <div class="col-10">{{ workItem.spot_name }}</div>
        </div>
        <div class="row ml-6 mr-6">
          <div class="col-2">
            {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.arrivalDateTime') }}
          </div>
          <div class="col-10">{{ workItem.start_time }}</div>
        </div>
        <div class="row ml-6 mr-6">
          <div class="col-2">
            {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.departureDateTime') }}
          </div>
          <div class="col-10">{{ workItem.end_time }}</div>
        </div>

        <v-divider class="mt-2 mb-2"></v-divider>

        <div class="row ml-6 mr-6 workload-update-table">
          <table>
            <thead>
              <tr>
                <th class="name-column">
                  {{
                    workItem.work == "refueling"?
                    i18n.global.t('pages.dailyReports.workloadUpdateModal.gasStationType') :
                    i18n.global.t('pages.dailyReports.workloadUpdateModal.productName')
                  }}
                </th>
                <th class="quantity-column">
                  {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.quantity') }}
                </th>
                <th class="unit-column">
                  {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.unit') }}
                </th>
                <th v-if="workItem.work !== 'refueling'" class="note-column">
                  {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.note') }}
                </th>
                <th class="operation-column">
                  {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.operation') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in items"
                :key="'workload-' + index"
                class="tr-nohover"
              >
                <td class="name-column">
                  <v-combobox
                    variant="underlined"
                    :items="selectableWorkloads"
                    v-model="item.name"
                    hide-details="auto"
                    :no-data-text="i18n.global.t('pages.dailyReports.workloadUpdateModal.selectproductName')"
                    :error-messages="errorMessages?.['workloads.' + index + '.name']"
                  />
                </td>
                <td class="quantity-column">
                  <v-text-field
                    variant="underlined"
                    v-model.number="item.quantity"
                    hide-details="auto"
                    :error-messages="errorMessages?.['workloads.' + index + '.quantity']"
                  ></v-text-field>
                </td>
                <td class="unit-column">
                  <v-combobox
                    v-if="workItem.work == 'loading' || workItem.work == 'unloading'"
                    variant="underlined"
                    :items="selectableUnits"
                    v-model="item.unit"
                    hide-details="auto"
                    :no-data-text="i18n.global.t('pages.dailyReports.workloadUpdateModal.inputUnit')"
                    :error-messages="errorMessages?.['workloads.' + index + '.unit']"
                  />
                  <v-select
                    v-else
                    variant="underlined"
                    :items="selectableUnits"
                    v-model="item.unit"
                    hide-details="auto"
                    :no-data-text="i18n.global.t('pages.dailyReports.workloadUpdateModal.inputUnit')"
                    :error-messages="errorMessages?.['workloads.' + index + '.unit']"
                  />
                </td>
                <td v-if="workItem.work !== 'refueling'" class="note-column">
                  <v-text-field
                    variant="underlined"
                    v-model="item.note"
                    hide-details="auto"
                    :error-messages="errorMessages?.['workloads.' + index + '.note']"
                  ></v-text-field>
                </td>
                <td class="operation-column">
                  <div class="td-flex">
                    <button
                      v-if="props.updateAuthority"
                      class="material-symbols-outlined remove-table-row-button"
                      @click="removeTableRow(item, index)"
                    >
                      delete
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="add-table-row-button-area">
            <button
              v-if="loadingState && items.length < MAX_TABLE_ROW[workItem.work]"
              class="material-symbols-outlined add-table-row-button mt-3"
              @click="addTableRow"
            >
              add
            </button>
          </div>
        </div>

        <v-divider class="mt-2 mb-2"></v-divider>

        <!-- FOOTER -->
        <div class="row ml-6 mr-6 mb-6 workload-update-button-area">
          <div class="d-flex justify-content-end">
            <v-btn
              class="reset-btn"
              @click="closeModal"
            >
              {{ i18n.global.t('pages.common.button.close') }}
            </v-btn>
            <v-btn
              v-if="props.updateAuthority"
              :loading="submitButtonState"
              :disabled="!loadingState"
              class="exec-btn ml-3"
              @click="submit"
            >
              {{ i18n.global.t('pages.common.button.conservation') }}
            </v-btn>
          </div>
        </div>
      </div>
    </GDialog>
  </template>
  <template v-else>
    <GDialog
      v-model="dialogState"
      max-width="50%"
      transition="custom-from-bottom-transition"
    >
      <div class="workload-update-error-modal m-3">
        <div>
          {{ i18n.global.t('pages.dailyReports.workloadUpdateModal.noSaveMessage') }}
        </div>
        <v-divider class="mt-2 mb-2"></v-divider>
        <div class="d-flex justify-content-end">
          <v-btn
            class="reset-btn"
            @click="closeModal"
          >
            {{ i18n.global.t('pages.common.button.close') }}
          </v-btn>
        </div>
      </div>
    </GDialog>
  </template>

  <button
    class="material-symbols-outlined box-add-button d-flex"
    @click="openModal"
  >
    <template
      v-if="workItem.work == 'loading' || workItem.work == 'unloading'"
    >
      box_add
    </template>
    <template
      v-if="workItem.work == 'refueling'"
    >
      local_gas_station
    </template>
  </button>
</template>


<!--
###########################################
 style
###########################################
-->
<style>
.max-width-50{
  max-width: 50%!important;
}
.workload-update-modal h4{
  margin: 0!important;
}
.workload-update-table table{
  width: 100%;
}
.workload-update-table th{
  background-color: rgb(210, 210, 210);
  border: solid 1px rgb(100, 100, 100);
}
.workload-update-table td,
.workload-update-table th
{
  padding: 8px;
}
.workload-update-table tr,
.workload-update-table td
{
  border: solid 1px rgb(150,150,150);
}
.workload-update-table .td-flex{
  display: flex;
  justify-content: center;
  align-items: center;
}
.workload-update-table td.name-column{
  width: 300px;
}
.workload-update-table td.quantity-column{
  width: 120px;
}
.workload-update-table td.unit-column{
  width: 120px;
}
.workload-update-table td.note-column{
  width: 340px;
}
.workload-update-table td.operation-column{
  width: 60px;
}

.workload-update-table td.quantity-column input{
  text-align: end;
}

.workload-update-table .add-table-row-button{
  color: white;
  background-color: blue;
  border-radius: 3px;
}
.workload-update-table .remove-table-row-button{
  color: white;
  background-color: red;
  border-radius: 3px;
}
.workload-update-modal-add-selectable-button{
  color: blue;
}
.workload-update-table .v-select .v-field__input{
  padding: 0!important;
  overflow: hidden;
}
.workload-update-table .v-select__selection-text,
.workload-update-table .v-select--single input
{
  padding-left: 12px;
}
.workload-update-table .v-select__selection{
  align-items: end!important;
}
</style>
